import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, Home, RefreshCw } from 'lucide-react'
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom'

export function ErrorBoundary() {
  const error = useRouteError()

  let errorMessage: string
  let errorStatus: number | undefined

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || error.data?.message || 'An error occurred'
    errorStatus = error.status
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else {
    errorMessage = 'An unexpected error occurred'
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-status-50/30 via-background to-background dark:from-blue-950/10 dark:via-background dark:to-background">
      <Card className="max-w-lg w-full mx-4 border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-danger-100 dark:bg-red-950/30 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-danger-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl">
              {errorStatus ? `Error ${errorStatus}` : 'Something went wrong'}
            </CardTitle>
          </div>
          <CardDescription className="text-base">{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button onClick={() => window.location.reload()} variant="outline" className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              Reload Page
            </Button>
            <Button
              asChild
              className="flex-1 bg-gradient-to-r from-brand-600 to-brand-600 hover:from-brand-700 hover:to-brand-700"
            >
              <Link to="/">
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Link>
            </Button>
          </div>
          {import.meta.env.DEV && error instanceof Error && error.stack && (
            <details className="mt-4 p-4 bg-muted rounded-lg">
              <summary className="cursor-pointer text-sm font-medium mb-2">
                Error Details (Dev Only)
              </summary>
              <pre className="text-xs overflow-auto max-h-64 whitespace-pre-wrap break-words">
                {error.stack}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
